import React from 'react';
import {
  Avatar,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  Typography,
  Tooltip,
  Button
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { get, map, find, isEmpty } from 'lodash';
import clsx from 'clsx';
import YouTubeIcon from '@mui/icons-material/YouTube';
import moment from 'moment';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import HelpIcon from '@mui/icons-material/Help';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(theme => ({
  '@keyframes pulsate': {
    '0%': {
      color: 'rgba(151,151,151, 0.2)'
    },
    '50%': {
      color: 'rgba(151,151,151, 1)'
    },
    '100%': {
      color: 'rgba(151,151,151, 0.2)'
    }
  },
  pulsating: {
    animation: '$pulsate 3s ease-in-out infinite'
  },
  oddRow: {
    // backgroundColor: theme.palette.background.paper,
    height: '2rem',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.hover}40`
    }
  },
  evenRow: {
    // backgroundColor: theme.palette.background.paper,
    height: '2rem',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.hover}40`
    }
  },
  tableHeader: {
    zIndex: 1
  },
  tableView: {
    borderRadius: '5px',
    overflow: 'auto'
  },
  tHeaderCol: {
    textAlign: 'center',
    // backgroundColor: '#f7f7f9',
    color: '#a6abbd',
    height: '4rem',
    fontSize: '12px',
    fontWeight: '400',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  tHeaderColStatic: {
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    height: '4rem',
    fontSize: '12px',
    fontWeight: '400'
  },
  tCol: {
    textAlign: 'center',
    color: theme.palette.background.dark,
    borderTop: '1px solid #cdd2d21A',
    fontSize: '12px'
  },
  teamDisplay: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  tBody: {
    marginTop: '-3px'
  },
  timeEntry: {
    display: 'flex',
    justifyContent: 'space-evenly'
  },
  banner: {
    width: '100%',
    background: 'none'
  },
  profileLink: {
    // color: theme.palette.background.dark,
    fontWeight: '700',
    '&:hover': {
      color: theme.palette.text.primary
    },
    fontSize: '12px'
  },
  videoLink: {
    color: theme.palette.secondary.main,
    '&:hover': {
      color: theme.palette.tertiary.secondary
    },
    cursor: 'pointer'
  },
  hidden: {
    display: 'none'
  },
  tooltip: {
    fontSize: '12px'
  },
  characterImage: {
    backgroundColor: '#ebeef1',
    boxShadow: '0px 0px 1px 1px #cdd2d2',
    border: '1px solid #cdd2d2',
    height: '35px',
    width: '35px',
    margin: '0.1rem'
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1rem',
    marginBottom: '1rem',
    color: theme.palette.text.primary
  },
  details: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  text: {
    fontSize: '12px',
    color: '#8c96a2'
  },
  tableContainer: {
    border: '1px solid #ced3d3',
    boxShadow: '0px 0px 1px 1px #cdd2d2'
  }
}));

function UserProfileSpeedrunTable(props) {
  const classes = useStyles();
  const { characterAssets } = useSelector(state => state.gameAssets);
  const tableHeaderIds = map(props.headers, 'id');

  const detailTooltips = {
    account_snapshot:
      'If character information is recorded for a run, you can view it here'
  };

  const entriesParsed = map(props.rows ?? [], entry => {
    const { time } = entry;
    const minutes = Math.floor(time / 60000);
    const seconds = (time % 60000) / 1000;
    return {
      ...entry,
      minutes,
      seconds
    };
  });

  return (
    <Box classes={{ root: classes.tableContainer }}>
      <Table className={classes.tableView} stickyHeader size="small">
        <TableHead className={classes.tableHeader}>
          <TableRow>
            {map(props.headers, header => {
              return (
                <TableCell
                  key={header.id}
                  id={header.id}
                  className={classes.tHeaderColStatic}
                >
                  {!!detailTooltips[header.id] && (
                    <Tooltip
                      classes={{ tooltip: classes.tooltip }}
                      title={detailTooltips[header.id]}
                    >
                      <Box classes={{ root: classes.details }}>
                        {header.label}
                        <Box mr={1} />
                        <HelpIcon style={{ color: '#ced3d3' }} />
                      </Box>
                    </Tooltip>
                  )}
                  {!detailTooltips[header.id] && header.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody className={classes.tBody}>
          {map(entriesParsed, (entry, idx) => {
            return (
              <TableRow
                className={clsx({
                  [classes.root]: true,
                  [classes.oddRow]: idx % 2,
                  [classes.evenRow]: !(idx % 2)
                })}
                key={`${entry._id}-${idx}`}
              >
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'speedrun_category'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'speedrun_category'
                    )
                  })}
                >
                  <Typography classes={{ root: classes.text }}>
                    {get(entry, 'speedrun_category')}
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'abyss_version'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'abyss_version'
                    )
                  })}
                >
                  <Typography classes={{ root: classes.text }}>
                    {get(entry, 'abyss_version')}
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'speedrun_subcategory'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'speedrun_subcategory'
                    )
                  })}
                >
                  <Typography classes={{ root: classes.text }}>
                    {get(entry, 'speedrun_subcategory')}
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'video_link'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'video_link'
                    )
                  })}
                >
                  <Tooltip title="Watch video">
                    <Box onClick={props.loadVideo(entry)}>
                      <YouTubeIcon classes={{ root: classes.videoLink }} />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(tableHeaderIds, h => h === 'team_1'),
                    [classes.hidden]: !find(tableHeaderIds, h => h === 'team_1')
                  })}
                >
                  <Box className={classes.teamDisplay}>
                    {map(entry.team_1, char => {
                      return (
                        <Avatar
                          src={get(characterAssets[char], 'avatar')}
                          key={`${idx}-${char}`}
                          alt={char}
                          className={classes.characterImage}
                        />
                      );
                    })}
                  </Box>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(tableHeaderIds, h => h === 'team_2'),
                    [classes.hidden]: !find(tableHeaderIds, h => h === 'team_2')
                  })}
                >
                  <Box className={classes.teamDisplay}>
                    {map(entry.team_2, char => {
                      if (!char) return null;

                      return (
                        <Avatar
                          src={get(characterAssets[char], 'avatar')}
                          key={`${idx}-2-${char}`}
                          alt={char}
                          className={classes.characterImage}
                        />
                      );
                    })}
                  </Box>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'created_at'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'created_at'
                    )
                  })}
                >
                  <Typography classes={{ root: classes.text }}>
                    {moment(get(entry, 'created_at')).format('MM/DD/YYYY')}
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'account_snapshot'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'account_snapshot'
                    )
                  })}
                >
                  {entry.account_snapshot && (
                    <Tooltip
                      title="Character Details Available"
                      classes={{ tooltip: classes.tooltip }}
                    >
                      <Button
                        onClick={props.loadSnapshot(entry.account_snapshot)}
                      >
                        <ZoomInIcon classes={{ root: classes.pulsating }} />
                      </Button>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {isEmpty(props.rows) && (
        <Box classes={{ root: classes.empty }}>
          <Typography variant="h4">No information to display.</Typography>
        </Box>
      )}
    </Box>
  );
}

UserProfileSpeedrunTable.propTypes = {
  headers: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortDir: PropTypes.string.isRequired,
  loadVideo: PropTypes.func.isRequired,
  loadSnapshot: PropTypes.func.isRequired
};

export default UserProfileSpeedrunTable;
